<script setup>
import { ref, computed } from 'vue'
import ChartView from '@/components/charts/ChartView.vue'
import { store } from '@/store/appStore.js'

const territoryFilter = ref('All')
const territories = computed(() => [...new Set(store.data.pob.value.map(r=>r.territory))].sort())
const months = computed(() => [...new Set(store.data.pob.value.map(r=>r.month))].sort())
const isAllView = computed(() => territoryFilter.value === 'All')
const territoryRows = computed(() => {
  if (isAllView.value) return months.value.map(month => {
    const rows = store.data.pob.value.filter(r=>r.month===month)
    const target=rows.reduce((s,r)=>s+r.targetValue,0), achieved=rows.reduce((s,r)=>s+r.achievedValue,0)
    const bm={}; rows.forEach(r=>r.productWiseBreakdown.forEach(b=>{bm[b.division]=(bm[b.division]||0)+b.value}))
    return { territory:'All', month, targetValue:target, achievedValue:achieved, achievementPct:Math.round(achieved/target*100), productWiseBreakdown:Object.entries(bm).map(([division,value])=>({division,value})) }
  })
  return store.data.pob.value.filter(r=>r.territory===territoryFilter.value).sort((a,b)=>a.month.localeCompare(b.month))
})
const trendChart = computed(() => ({ labels: territoryRows.value.map(r=>r.month), datasets: [{ label:'Achievement %', data: territoryRows.value.map(r=>r.achievementPct), borderColor:'#0F3D3E', backgroundColor:'rgba(15,61,62,0.08)', tension:0.3, fill:true, pointRadius:3 }] }))
const breakdown = computed(() => territoryRows.value[territoryRows.value.length-1]?.productWiseBreakdown||[])
const breakdownChart = computed(() => ({ labels: breakdown.value.map(d=>d.division), datasets: [{ label:'Value (USD)', data: breakdown.value.map(d=>d.value), backgroundColor:['#0F3D3E','#C99A2E','#3A9B5C','#D9752E','#6B7075'] }] }))

// Phase 9: timeframe toggle. Achievement % is a ratio and is timeframe-
// invariant, so only the $ Target/Achieved columns in the ranking table
// scale — the trend chart above always stays monthly (the only real
// granularity behind the data), and we say so explicitly rather than
// implying we have real daily/quarterly click-through history we don't.
const TIMEFRAMES = ['Daily', 'Monthly', 'Quarterly', 'Annual']
const timeframe = ref('Monthly')
const TIMEFRAME_MULTIPLIER = { Daily: 1/30, Monthly: 1, Quarterly: 3, Annual: 12 }
const timeframeNote = computed(() => ({
  Daily: 'Latest month ÷ 30 (calendar-day average)',
  Monthly: 'As tracked — no scaling applied',
  Quarterly: 'Latest month × 3',
  Annual: 'Latest month × 12 (annualized run-rate, not a full 12-month history)',
}[timeframe.value]))

const summary = computed(() => {
  const byTerr = {}
  store.data.pob.value.forEach(r=>{ if(!byTerr[r.territory]||r.month>byTerr[r.territory].month) byTerr[r.territory]=r })
  const mult = TIMEFRAME_MULTIPLIER[timeframe.value]
  return Object.values(byTerr)
    .map(r => ({ ...r, scaledTarget: Math.round(r.targetValue*mult), scaledAchieved: Math.round(r.achievedValue*mult) }))
    .sort((a,b)=>b.achievementPct-a.achievementPct)
})

// --- Pharmacy Leaderboard ----------------------------------------------
// No per-pharmacy POB target exists in the data model (POB is territory-
// level only). Reuses the same actual-vs-potential ratio Business Potential
// already computes — same formula, not a fresh fabricated metric — as a
// pharmacy-level stand-in for "target achievement %".
const CEIL = { A:5000, B:3000, C:1500 }
const pharmacyLeaderboard = computed(() => {
  const byPh = {}
  store.data.orders.value.forEach(o => { byPh[o.customerId] = (byPh[o.customerId]||0) + o.orderValue })
  return store.data.pharmacies.value.map(p => {
    const potentialValue = Math.round((CEIL[p.category]||2000) * (p.potentialScore/100))
    const actual = Math.round(byPh[p.customerId] || 0)
    const rawPct = potentialValue ? Math.round((actual/potentialValue)*100) : 0
    // Display cap: an uncapped ratio can read as absurd (900%+) for a small
    // number of high-volume accounts against a modest category ceiling —
    // real dashboards cap over-achievement display rather than show a
    // number that looks like broken data. The underlying rawPct is kept
    // for sorting so true over-performers still rank at the top.
    const achievementPct = Math.min(rawPct, 150)
    const capped = rawPct > 150
    return { ...p, actual, potentialValue, achievementPct, capped, rawPct }
  }).sort((a,b) => b.rawPct - a.rawPct).slice(0, 10)
})

// --- Sales Supervisor Coverage Matrix ------------------------------------
// "Planned" = every Category A pharmacy should get at least one visit this
// period. "Actual" = pharmacies with a real Phase 7 visit note logged this
// session — genuine data, not fabricated visit history, so this is 0 by
// default until the user actually logs visits from Pharmacy Master.
const showCoverage = ref(false)
const coverageByTerritory = computed(() => {
  const byTerr = {}
  store.data.pharmacies.value.filter(p => p.category === 'A').forEach(p => {
    byTerr[p.territory] = byTerr[p.territory] || { territory: p.territory, planned: [], visited: [] }
    byTerr[p.territory].planned.push(p)
    if ((store.state.visitNotes[p.customerId]||[]).length > 0) byTerr[p.territory].visited.push(p)
  })
  return Object.values(byTerr).map(t => ({ ...t, pct: t.planned.length ? Math.round(t.visited.length/t.planned.length*100) : 0 }))
})
const unvisitedCategoryA = computed(() => store.data.pharmacies.value.filter(p => p.category === 'A' && (store.state.visitNotes[p.customerId]||[]).length === 0))
</script>
<template>
  <div>
    <div class="card module-header"><h2>Purchase Order Booking across {{ territories.length }} territories</h2></div>

    <div class="card" style="padding:18px 20px;margin-bottom:16px;">
      <div class="toolbar" style="border-bottom:none;padding:0 0 12px;flex-wrap:wrap;">
        <select class="filter-select" v-model="territoryFilter"><option>All</option><option v-for="t in territories" :key="t">{{ t }}</option></select>
        <span class="result-count">{{ isAllView?'All territories combined':territoryFilter }} — trailing 6 months (always monthly)</span>
      </div>
      <div class="split-main-side">
        <div><div class="eyebrow" style="margin-bottom:8px;">Achievement % Trend</div><ChartView type="line" :labels="trendChart.labels" :datasets="trendChart.datasets" :height="220" :key="'trend-'+territoryFilter" /></div>
        <div><div class="eyebrow" style="margin-bottom:8px;">Latest Month — Product-wise POB</div><ChartView type="doughnut" :labels="breakdownChart.labels" :datasets="breakdownChart.datasets" :height="220" :key="'breakdown-'+territoryFilter" /></div>
      </div>
    </div>

    <div class="card" style="padding:16px 20px;margin-bottom:16px;">
      <div class="toolbar" style="border-bottom:none;padding:0 0 10px;flex-wrap:wrap;">
        <div style="display:flex;gap:4px;">
          <button v-for="tf in TIMEFRAMES" :key="tf" class="filter-select" :style="timeframe===tf?'background:var(--color-primary-dark);color:#fff;border-color:var(--color-primary-dark);':'cursor:pointer;'" @click="timeframe=tf">{{ tf }}</button>
        </div>
        <span class="result-count">{{ timeframeNote }}</span>
      </div>
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>Territory</th><th>Month</th><th>Target (USD)</th><th>Achieved (USD)</th><th>Achievement %</th></tr></thead>
          <tbody>
            <tr v-for="r in summary" :key="r.territory">
              <td class="cell-strong">{{ r.territory }}</td><td class="cell-muted">{{ r.month }}</td>
              <td class="cell-muted">${{ r.scaledTarget.toLocaleString() }}</td><td class="cell-muted">${{ r.scaledAchieved.toLocaleString() }}</td>
              <td :style="r.achievementPct>=100?'color:var(--status-green);font-weight:700;':r.achievementPct<85?'color:var(--status-red);font-weight:700;':''">{{ r.achievementPct }}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="card" style="margin-bottom:16px;">
      <div class="toolbar"><span class="result-count">Pharmacy Leaderboard — top 10 by target achievement %</span></div>
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>#</th><th>Pharmacy</th><th>Territory</th><th>Actual (USD)</th><th>Potential (USD)</th><th>Achievement %</th></tr></thead>
          <tbody>
            <tr v-for="(p,i) in pharmacyLeaderboard" :key="p.customerId">
              <td class="cell-muted">{{ i+1 }}</td><td class="cell-strong">{{ p.pharmacyName }}</td><td class="cell-muted">{{ p.territory }}</td>
              <td class="cell-muted">${{ p.actual.toLocaleString() }}</td><td class="cell-muted">${{ p.potentialValue.toLocaleString() }}</td>
              <td :style="p.achievementPct>=100?'color:var(--status-green);font-weight:700;':''">{{ p.capped ? '150%+' : p.achievementPct + '%' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="card">
      <div class="toolbar">
        <button class="filter-select" style="cursor:pointer;" @click="showCoverage=!showCoverage">{{ showCoverage?'Hide':'Show' }} Sales Supervisor Coverage Matrix</button>
      </div>
      <div v-if="showCoverage" style="padding:16px 20px;">
        <p class="muted" style="font-size:12.5px;margin:0 0 14px;">Coverage target: every Category A pharmacy visited at least once. "Visited" reflects real field visit notes logged via Pharmacy Master this session.</p>
        <table class="data-table" style="margin-bottom:16px;">
          <thead><tr><th>Territory</th><th>Category A Pharmacies</th><th>Visited</th><th>Coverage %</th></tr></thead>
          <tbody>
            <tr v-for="t in coverageByTerritory" :key="t.territory">
              <td class="cell-strong">{{ t.territory }}</td><td class="cell-muted">{{ t.planned.length }}</td><td class="cell-muted">{{ t.visited.length }}</td>
              <td :style="t.pct>=100?'color:var(--status-green);font-weight:700;':t.pct===0?'color:var(--status-red);font-weight:700;':''">{{ t.pct }}%</td>
            </tr>
          </tbody>
        </table>
        <div class="eyebrow" style="margin-bottom:8px;">Unvisited Category A Pharmacies ({{ unvisitedCategoryA.length }})</div>
        <div v-if="!unvisitedCategoryA.length" class="muted" style="font-size:13px;">All Category A pharmacies have been visited this session.</div>
        <div v-else class="impacted-chip-list"><span v-for="p in unvisitedCategoryA" :key="p.customerId" class="impacted-chip">{{ p.pharmacyName }}</span></div>
      </div>
    </div>
  </div>
</template>